<template>
  <v-dialog
    v-model="dialog"
    max-width="600"
    scrollable
    @click:outside="$emit('cancel')"
  >
    <template #default>
      <v-card style="max-height: 80vh; overflow-y: auto;">
        <v-card-title>
          {{ settingCourseName }} 延長設計
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <!-- タブにしたい, 一つ目指名, 2つ目アイテム -->
            <v-tabs
              v-model="tab"
            >
              <v-tab
                v-for="item in tabItems"
                :key="item.label"
                :value="item.label"
              >
                {{ item.label }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="tab">
              <v-tab-item>
                <div v-if="nominations.length > 0">
                  <dual-vendible-selector
                    v-for="(pair, index) in nominationSelectorPairs"
                    :key="`nomination-pair-${index}`"
                    :items="nominations"
                    from-label="対象の指名"
                    to-label="延長後の指名"
                    add-button-text="延長候補に追加"
                    :show-remove="nominationSelectorPairs.length > 0"
                    :disabled="loading"
                    :from-value="pair.fromValue"
                    :to-value="pair.toValue"
                    :pair-container="pair"
                    :duplicate-pairs="duplicateNominationPairs"
                    @add="(data) => handleNominationAdd(data, index)"
                    @remove="toggleNominationSelectorPair(index)"
                    @update:from-value="pair.fromValue = $event"
                    @update:to-value="pair.toValue = $event"
                  />

                  <v-row class="ma-2">
                    <v-col
                      cols="12"
                      class="text-center"
                    >
                      <v-btn
                        color="secondary"
                        outlined
                        :disabled="loading"
                        @click="addNominationSelectorPair"
                      >
                        <v-icon
                          small
                        >
                          {{ icons.mdiPlus }}
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
                <v-alert
                  v-else
                  type="info"
                  outlined
                  class="text-caption"
                >
                  指名が登録されていません
                </v-alert>
              </v-tab-item>

              <v-tab-item>
                <div v-if="items.length > 0">
                  <dual-vendible-selector
                    v-for="(pair, index) in itemSelectorPairs"
                    :key="`item-pair-${index}`"
                    :items="items"
                    from-label="対象のアイテム"
                    to-label="延長後のアイテム"
                    add-button-text="延長候補に追加"
                    :show-remove="itemSelectorPairs.length > 0"
                    :disabled="loading"
                    :from-value="pair.fromValue"
                    :to-value="pair.toValue"
                    :pair-container="pair"
                    @add="(data) => handleItemAdd(data, index)"
                    @remove="toggleItemSelectorPair(index)"
                    @update:from-value="pair.fromValue = $event"
                    @update:to-value="pair.toValue = $event"
                  />

                  <v-row class="ma-2">
                    <v-col
                      cols="12"
                      class="text-center"
                    >
                      <v-btn
                        color="secondary"
                        outlined
                        :disabled="loading"
                        @click="addItemSelectorPair"
                      >
                        <v-icon
                          small
                        >
                          {{ icons.mdiPlus }}
                        </v-icon>
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
                <v-alert
                  v-else
                  type="info"
                  outlined
                  class="text-caption"
                >
                  アイテムが登録されていません
                </v-alert>
              </v-tab-item>
            </v-tabs-items>
          </div>

          <v-divider class="my-4"></v-divider>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!hasChanges || duplicateNominationPairs || duplicateItemPairs"
            :loading="loading"
            @click="saveChanges"
          >
            保存
          </v-btn>

          <v-btn
            text
            @click="
              dialog = false
              $emit('cancel')
            "
          >
            キャンセル
          </v-btn>
        </v-card-actions>

        <v-alert
          v-if="duplicateNominationPairs || duplicateItemPairs"
          type="error"
          dense
          class="ma-2"
        >
          重複したペアが存在します。同じペアを複数登録することはできません。
        </v-alert>
      </v-card>
    </template>
  </v-dialog>
</template>

<script>
import {
  ref, computed, nextTick, watch,
} from '@vue/composition-api'
import {
  mdiClose, mdiTimerSandFull, mdiPlus,
} from '@mdi/js'
import { useVModel } from '@vueuse/core'
import DualVendibleSelector from './DualVendibleSelector.vue'

export default {
  components: {
    DualVendibleSelector,
  },
  props: {
    isPassiveCourseExtensionsDialogOn: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    passiveCourseExtensions: {
      type: Array,
      default: () => [],
    },
    availableCourses: {
      type: Array,
      default: () => [],
    },
    nominations: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    selectedVendible: {
      type: Object,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const dialog = useVModel(props, 'isPassiveCourseExtensionsDialogOn')
    const passiveCourseExtensions = useVModel(props, 'passiveCourseExtensions')
    const availableCourses = useVModel(props, 'availableCourses')
    const tab = ref('指名')
    const loading = ref(false)
    const selectedCourseId = ref(null)
    const initialExtensions = ref([])
    const nominationSelectorPairs = ref([{ id: null, fromValue: null, toValue: null }])
    const itemSelectorPairs = ref([{ id: null, fromValue: null, toValue: null }])

    const tabItems = computed(() => {
      return [
        {
          label: '指名',
          resourceType: 'nomination',
        },
        {
          label: 'アイテム',
          resourceType: 'item',
        },
      ]
    })

    const formatNumber = value => {
      if (!value) return '0'

      return Number(value).toLocaleString()
    }

    const handleNominationAdd = ({ from, to }, pairIndex) => {
      const existingExtension = passiveCourseExtensions.value.find(ext => ext.attributes.extenderId === to.id && ext.attributes.extendeeId === from.id)

      if (!existingExtension) {
        const pair = nominationSelectorPairs.value[pairIndex]
        const newExtension = {
          id: pair.ruleId || null, // 既存ルールのidまたはnull（新規）
          attributes: {
            id: pair.ruleId || null,
            extendeeId: from.id,
            extenderId: to.id,
            beforeNominationId: from.id,
            afterNominationId: to.id,
            afterCourseId: pair.afterCourseId || props.selectedVendible?.id,
          },
        }
        passiveCourseExtensions.value.push(newExtension)
      }
    }

    const handleItemAdd = ({ from, to }, pairIndex) => {
      const existingExtension = passiveCourseExtensions.value.find(ext => ext.attributes.extenderId === to.id && ext.attributes.extendeeId === from.id)

      if (!existingExtension) {
        const pair = itemSelectorPairs.value[pairIndex]
        const newExtension = {
          id: pair.ruleId || null, // 既存ルールのidまたはnull（新規）
          attributes: {
            id: pair.ruleId || null,
            extendeeId: from.id,
            extenderId: to.id,
            beforeItemId: from.id,
            afterItemId: to.id,
            afterCourseId: pair.afterCourseId || props.selectedVendible?.id,
          },
        }
        passiveCourseExtensions.value.push(newExtension)
      }
    }

    const addNominationSelectorPair = () => {
      // 新規ペアはidをnullにして新規作成扱いにする
      const existingIds = nominationSelectorPairs.value
        .map(p => p.id)
        .filter(id => id !== null)
      const newTempId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1

      nominationSelectorPairs.value.push({
        id: null, // 新規作成扱い
        tempId: newTempId, // 一時的なid（削除などで使用）
        fromValue: null,
        toValue: null,
        ruleId: null,
        beforeNominationId: null,
        afterNominationId: null,
        afterCourseId: null,
      })
    }

    const toggleNominationSelectorPair = index => {
      if (nominationSelectorPairs.value.length > 0) {
        // idがnullのものを削除するときは要素を削除する
        if (nominationSelectorPairs.value[index].id === null) {
          nominationSelectorPairs.value.splice(index, 1)
        } else {
          nominationSelectorPairs.value[index]._destroy = !nominationSelectorPairs.value[index]._destroy
        }
      }
    }

    const addItemSelectorPair = () => {
      // 新規ペアはidをnullにして新規作成扱いにする
      const existingIds = itemSelectorPairs.value
        .map(p => p.id)
        .filter(id => id !== null)
      const newTempId = existingIds.length > 0 ? Math.max(...existingIds) + 1 : 1

      itemSelectorPairs.value.push({
        id: null, // 新規作成扱い
        tempId: newTempId, // 一時的なid（削除などで使用）
        fromValue: null,
        toValue: null,
        ruleId: null,
        beforeItemId: null,
        afterItemId: null,
        afterCourseId: null,
      })
    }

    const toggleItemSelectorPair = index => {
      if (itemSelectorPairs.value.length > 0) {
        if (itemSelectorPairs.value[index].id === null) {
          itemSelectorPairs.value.splice(index, 1)
        } else {
          itemSelectorPairs.value[index]._destroy = !itemSelectorPairs.value[index]._destroy
        }
      }
    }

    const resetSelectorPairs = () => {
      nominationSelectorPairs.value = [{ id: 1, fromValue: null, toValue: null }]
      itemSelectorPairs.value = [{ id: 1, fromValue: null, toValue: null }]
    }

    const findNominationById = nominationId => {
      return props.nominations.find(nomination => nomination.attributes.id === nominationId)
    }

    const findItemById = itemId => {
      return props.items.find(item => item.attributes.id === itemId)
    }

    const initializeNominationSelectorPairsFromRules = () => {
      const rules = props.selectedVendible?.attributes?.nominationExtensionRules || []

      if (rules.length > 0) {
        nominationSelectorPairs.value = rules.map((rule, index) => {
          const fromNomination = findNominationById(rule.before_nomination_id)
          const toNomination = findNominationById(rule.after_nomination_id)

          return {
            id: rule.id || null, // idがない場合はnullで新規作成扱い
            ruleId: rule.id, // 既存ルールのid（削除時などに使用）
            fromValue: fromNomination || null,
            toValue: toNomination || null,
            beforeNominationId: rule.before_nomination_id,
            afterNominationId: rule.after_nomination_id,
            afterCourseId: rule.after_course_id,
            _destroy: false,
          }
        })
      } else {
        // ルールがない場合は初期状態にリセット
        nominationSelectorPairs.value = [{ id: null, fromValue: null, toValue: null }]
      }
    }

    const initializeItemSelectorPairsFromRules = () => {
      const rules = props.selectedVendible?.attributes?.itemExtensionRules || []

      if (rules.length > 0) {
        itemSelectorPairs.value = rules.map((rule, index) => {
          const fromItem = findItemById(rule.before_item_id)
          const toItem = findItemById(rule.after_item_id)

          return {
            id: rule.id || null, // idがない場合はnullで新規作成扱い
            ruleId: rule.id, // 既存ルールのid（削除時などに使用）
            fromValue: fromItem || null,
            toValue: toItem || null,
            beforeItemId: rule.before_item_id,
            afterItemId: rule.after_item_id,
            afterCourseId: rule.after_course_id,
            _destroy: false,
          }
        })
      } else {
        // ルールがない場合は初期状態にリセット
        itemSelectorPairs.value = [{ id: null, fromValue: null, toValue: null }]
      }
    }

    const selectedCourseName = computed(() => {
      if (!selectedCourseId.value) return ''

      return availableCourses.value.find(course => course.attributes.id === selectedCourseId.value)?.attributes?.name
    })

    const hasChanges = computed(() => {
      if (initialExtensions.value.length !== passiveCourseExtensions.value.length) return true

      return !passiveCourseExtensions.value.every(ext => initialExtensions.value.some(initial => initial.attributes.id === ext.attributes.id))
    })

    const getCourseName = courseId => {
      const course = availableCourses.value.find(c => c.attributes.id === courseId)

      return course?.attributes?.name || '不明なコース'
    }

    const getSellingPrice = courseId => {
      const course = availableCourses.value.find(c => c.attributes.id === courseId)

      return course?.attributes?.sellingPrice || 0
    }

    const duplicateNominationPairs = computed(() => {
      if (nominationSelectorPairs.value.length === 0) return false
      const filteredPairs = nominationSelectorPairs.value.filter(pair => pair.fromValue !== null && pair.toValue !== null)

      return filteredPairs.some(pair => filteredPairs.some(p => p.fromValue?.id === pair.fromValue?.id
        && p.toValue?.id === pair.toValue?.id
        && (p.id !== pair.id || (p.id === null && pair.id === null && p !== pair))))
    })

    const duplicateItemPairs = computed(() => {
      if (itemSelectorPairs.value.length === 0) return false
      const filteredPairs = itemSelectorPairs.value.filter(pair => pair.fromValue !== null && pair.toValue !== null)

      return filteredPairs.some(pair => filteredPairs.some(p => p.fromValue?.id === pair.fromValue?.id
        && p.toValue?.id === pair.toValue?.id
        && (p.id !== pair.id || (p.id === null && pair.id === null && p !== pair))))
    })

    const settingCourseName = computed(() => {
      return props.selectedVendible.attributes.name
    })

    const removeExtension = extension => {
      passiveCourseExtensions.value = passiveCourseExtensions.value.filter(ext => ext.attributes.id !== extension.attributes.id || ext.attributes.extenderId !== extension.attributes.extenderId)
    }

    const saveChanges = async () => {
      emit('update', {
        resourceType: tabItems.value[tab.value].resourceType,
        nominationExtensionRulesAttributes: nominationSelectorPairs.value.map(nomination => ({
          id: nomination.id,
          beforeNominationId: nomination.fromValue?.id,
          afterNominationId: nomination.toValue?.id,
          _destroy: nomination._destroy,
        })),
        itemExtensionRulesAttributes: itemSelectorPairs.value.map(item => ({
          id: item.id,
          beforeItemId: item.fromValue?.id,
          afterItemId: item.toValue?.id,
          _destroy: item._destroy,
        })),
      })
    }

    // ダイアログが開かれた時の初期値を保存
    const initializeExtensions = () => {
      initialExtensions.value = [...passiveCourseExtensions.value]
    }

    // ダイアログの開閉を監視してリセット
    watch(dialog, (newValue, oldValue) => {
      if (oldValue && !newValue) {
        // ダイアログが閉じられた時
        resetSelectorPairs()
      }
      if (newValue && !oldValue) {
        // ダイアログが開かれた時
        initializeNominationSelectorPairsFromRules()
        initializeItemSelectorPairsFromRules()
      }
    })

    // selectedVendibleの変更を監視
    watch(() => props.selectedVendible, () => {
      if (dialog.value) {
        initializeNominationSelectorPairsFromRules()
        initializeItemSelectorPairsFromRules()
      }
    }, { deep: true })

    return {
      dialog,
      tab,
      selectedCourseId,
      selectedCourseName,
      hasChanges,
      getCourseName,
      removeExtension,
      saveChanges,
      initializeExtensions,
      formatNumber,
      handleNominationAdd,
      handleItemAdd,
      nominationSelectorPairs,
      itemSelectorPairs,
      addNominationSelectorPair,
      toggleNominationSelectorPair,
      addItemSelectorPair,
      toggleItemSelectorPair,
      resetSelectorPairs,
      findNominationById,
      findItemById,
      initializeNominationSelectorPairsFromRules,
      initializeItemSelectorPairsFromRules,
      // computed
      settingCourseName,
      tabItems,
      duplicateNominationPairs,
      duplicateItemPairs,
      icons: {
        mdiClose,
        mdiTimerSandFull,
        mdiPlus,
      },
    }
  },
}
</script>
